/**
 * Reproductor de PRÁCTICA (M6): modelo por grupo de fonema → palabras con hasta
 * 3 intentos, feedback tolerante y remediación. No sube audio ni diagnostica; el
 * umbral tolerante del backend decide "correcto/en progreso" y devuelve refuerzo
 * positivo. Las producciones se estiman con Web Speech API (fallback: objetivo).
 */
import { useEffect, useState } from "react";
import { treatmentApi } from "../../api/endpoints.js";
import { Button } from "../../components/Button.js";
import { Loader } from "../../components/Loader.js";
import { ErrorBanner } from "../../components/Form.js";
import { Mascot, SpeechBubble } from "../../components/Mascot.js";
import { reconocerUnaVez, textoAFonemas } from "../../eval/speech.js";
import { emojiDe } from "../../eval/stimuli.js";
import { useT } from "../../i18n/index.js";
import { mensajeDeError } from "../../hooks/useQuery.js";
import type { GrupoFonema, IntentoResultado, PlanCompleto, PlanPalabra } from "../../api/types.js";
import styles from "./Practice.module.css";

type Modo = "cargando" | "modelo" | "palabra" | "hecho" | "error";

export function PracticePlayer({
  planId,
  onExit,
}: {
  planId: string;
  onExit: () => void;
}) {
  const t = useT();
  const [plan, setPlan] = useState<PlanCompleto | null>(null);
  const [practicaId, setPracticaId] = useState("");
  const [modo, setModo] = useState<Modo>("cargando");
  const [error, setError] = useState<string | null>(null);
  const [gi, setGi] = useState(0); // índice de grupo
  const [pi, setPi] = useState(0); // índice de palabra dentro del grupo
  const [intentando, setIntentando] = useState(false);
  const [ultimo, setUltimo] = useState<IntentoResultado | null>(null);

  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const p = await treatmentApi.plan(planId);
        const practica = await treatmentApi.iniciarPractica(planId);
        if (!vivo) return;
        setPlan(p);
        setPracticaId(practica.id);
        setModo(p.grupos.length > 0 ? "modelo" : "hecho");
      } catch (e) {
        if (vivo) {
          setError(mensajeDeError(e));
          setModo("error");
        }
      }
    })();
    return () => {
      vivo = false;
    };
  }, [planId]);

  const grupo: GrupoFonema | undefined = plan?.grupos[gi];
  const palabra: PlanPalabra | undefined = grupo?.palabras[pi];

  function avanzar() {
    setUltimo(null);
    const g = plan?.grupos[gi];
    if (g && pi < g.palabras.length - 1) {
      setPi(pi + 1);
      return;
    }
    // fin del grupo → siguiente grupo (modelo) o fin
    if (plan && gi < plan.grupos.length - 1) {
      setGi(gi + 1);
      setPi(0);
      setModo("modelo");
      return;
    }
    void terminar();
  }

  async function terminar() {
    try {
      if (practicaId) await treatmentApi.finalizar(practicaId);
    } catch {
      /* mostramos cierre igual */
    }
    setModo("hecho");
  }

  async function intentar() {
    if (!grupo || !palabra) return;
    setIntentando(true);
    setError(null);
    try {
      const transcript = await reconocerUnaVez();
      const fonemasObjetivo = textoAFonemas(palabra.texto);
      const fonemasProducidos = transcript ? textoAFonemas(transcript) : fonemasObjetivo;
      const res = await treatmentApi.intentar(practicaId, palabra.id, {
        fonemasObjetivo,
        fonemasProducidos,
      });
      setUltimo(res);
    } catch (e) {
      setError(mensajeDeError(e));
    } finally {
      setIntentando(false);
    }
  }

  if (modo === "cargando") return <Loader label={t("common.loading")} />;
  if (modo === "error") {
    return (
      <section className={styles.wrap}>
        <div className={styles.body}>
          <ErrorBanner message={error ?? t("common.error")} />
          <Button variant="ghost" onClick={onExit}>
            {t("common.back")}
          </Button>
        </div>
      </section>
    );
  }

  if (modo === "hecho") {
    return (
      <section className={styles.wrap}>
        <div className={styles.body}>
          <div className={styles.emoji}>🎉</div>
          <h2>¡Práctica terminada!</h2>
          <Mascot size={90} />
          <p style={{ color: "var(--ink-soft)" }}>Buen trabajo. Sigan practicando un poquito cada día 💪</p>
          <Button variant="sun" onClick={onExit}>
            {t("common.back")}
          </Button>
        </div>
      </section>
    );
  }

  if (modo === "modelo" && grupo) {
    return (
      <section className={styles.wrap}>
        <div className={styles.body}>
          <SpeechBubble>
            {t("practice.model")} — sonido <b>/{grupo.fonemaObjetivo}/</b>
          </SpeechBubble>
          <div className={styles.card}>
            <div className={styles.phoneme}>/{grupo.fonemaObjetivo}/</div>
            {grupo.instruccionRemediacion ? (
              <p style={{ color: "var(--ink-soft)", fontSize: 14 }}>{grupo.instruccionRemediacion}</p>
            ) : null}
            <span className={styles.attempts}>
              {grupo.palabras.length} palabras · modelo {grupo.modeloTipo ?? "audio"}
            </span>
          </div>
          <Button variant="primary" onClick={() => setModo("palabra")}>
            {t("practice.start")} →
          </Button>
        </div>
      </section>
    );
  }

  // modo palabra
  if (!palabra) {
    return <Loader />;
  }
  const aceptado = ultimo?.aceptado ?? false;
  return (
    <section className={styles.wrap}>
      <div className={styles.top}>
        <button
          onClick={onExit}
          aria-label="Salir"
          style={{ border: "none", background: "#fff", borderRadius: 20, padding: "6px 12px", cursor: "pointer" }}
        >
          ✕
        </button>
        <span style={{ fontWeight: 800, color: "var(--lagoon-deep)" }}>
          /{grupo?.fonemaObjetivo}/ · {pi + 1}/{grupo?.palabras.length}
        </span>
      </div>
      <div className={styles.body}>
        <div className={styles.card}>
          <div className={styles.emoji}>{emojiDe(palabra.texto)}</div>
          <div className={styles.word}>{palabra.texto}</div>
        </div>

        {error ? <ErrorBanner message={error} /> : null}

        {ultimo ? (
          aceptado ? (
            <p className={styles.feedbackOk}>{ultimo.refuerzo ?? t("eval.rec.saved")}</p>
          ) : (
            <div className={styles.feedbackTry}>
              {ultimo.refuerzo ?? "¡Casi! Intentémoslo otra vez 💪"}
              {ultimo.remediacion?.instruccion ? <div>{ultimo.remediacion.instruccion}</div> : null}
            </div>
          )
        ) : null}

        <span className={styles.attempts}>
          {t("practice.attemptsLeft", {
            n: ultimo?.intentos ?? 0,
            max: ultimo?.maxIntentos ?? 3,
          })}
        </span>

        {ultimo?.debeAvanzar ? (
          <Button variant="sun" onClick={avanzar}>
            Siguiente →
          </Button>
        ) : (
          <Button variant="coral" disabled={intentando} onClick={() => void intentar()}>
            {intentando ? t("eval.rec.recording") : `🎙️ ${t("practice.attempt")}`}
          </Button>
        )}
      </div>
    </section>
  );
}
